import type { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import { api, ApiError, type VoiceInfo } from '../sync/api';
import {
  clipLength, setClipVoiceChoice, setNarration, setVoiceAudio, setVoiceText, voiceOf, voiceSig, voiceSpans, voiceState,
  VOICE_TEXT_MAX, type VideoClip, type VideoProject, type VoiceChoice, type VoiceProvider, type VoiceState
} from '../video/project';
import { $, $in, setPressed } from './dom';
import { makeText } from './objects';
import { isOpen, openSheet } from './sheets';
import { toast } from './toast';
import type { VideoCtx } from './videoView';

/*
 * เสียงพากย์ AI (0016, choices 1ก 2ค 3ก 4ก 5ก 6ก 7ก) — the "พากย์" panel of the video screen:
 *   the video's voice   Gemini or ElevenLabs (2ค), a voice from the service's list, and how to speak (Gemini)
 *   each scene          its words, "สร้างเสียง" (the server asks the service and keeps the file, 5ก), "ฟัง", and its
 *                       own voice if wanted (7ก); a scene grows to fit its voice (1ก, src/video/project.ts fitVoices)
 *   subtitles           the words over the video while they are spoken (6ก, syncSubs) — text objects marked `sub`
 *   the cap             this month's spend of the group and, for admins, the cap (4ก: admins + members with the right)
 * Every refusal says why: no voice chosen, no words, a key not set, the cap, the service's own error.
 */

/** dollars for people: small amounts keep 3 decimals */
const usd = (n: number) => n < 1 ? n.toFixed(3) : n.toFixed(2);
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
type SubText = fabric.Object & { sub?: string; text?: string; vStart?: number; vEnd?: number; vFade?: boolean; name?: string };

/**
 * 6ก: one subtitle (a text object marked `sub` = the clip's id) for each scene with a voice, shown while it speaks;
 * its words follow the scene's words, its time follows the scene (moves, trims, a longer voice). A subtitle whose
 * scene has no voice any more is taken away; all of them when subtitles are off. Position and look stay as edited.
 * Called inside the same undo step as the video edit (src/ui/videoView.ts commit).
 */
export function syncSubs(ed: Editor, p: VideoProject): void {
  const objs = ed.userObjects() as SubText[];
  const want = new Map(p.narration.subs ? voiceSpans(p).map(v => [p.clips[v.index].id, { v, text: p.clips[v.index].voice!.text.trim() }]) : []);
  for (const o of objs) {
    if (!o.sub) continue;
    if (!want.has(o.sub)) { ed.canvas.remove(o); continue; }
    const w = want.get(o.sub)!;
    const r = (x: number) => Math.round(x * 1000) / 1000;
    if (o.text !== w.text) { (o as fabric.Textbox).set('text', w.text); (o as fabric.Textbox).initDimensions(); }
    o.set({ vStart: r(w.v.start), vEnd: r(w.v.end), vFade: false } as Partial<fabric.Object>);
    o.setCoords();
    want.delete(o.sub);
  }
  // a new subtitle: white on a dark band, across the lower part of the frame
  const W = ed.doc.w, H = ed.doc.h, u = Math.min(W, H) / 1080;
  for (const [id, w] of want) {
    const t = makeText(w.text, {
      fontFamily: 'Sarabun', fontWeight: 600, fontSize: 52 * u, width: W * 0.84, fill: '#ffffff', textAlign: 'center',
      backgroundColor: 'rgba(0,0,0,0.55)', lineHeight: 1.3
    });
    t.set({ left: (W - (t.width ?? 0)) / 2, top: H * 0.8 - (t.height ?? 0) / 2 });
    (t as SubText).set({ sub: id, name: `ซับไตเติล ฉาก ${w.v.index + 1}`, vStart: w.v.start, vEnd: w.v.end, vFade: false } as Partial<fabric.Object>);
    ed.canvas.add(t);
  }
}

/** how long a voice file is (seconds): from the server for a WAV, measured by the browser for an MP3 */
function measure(url: string): Promise<number> {
  return new Promise((res, rej) => {
    const a = new Audio();
    a.preload = 'metadata';
    const done = (fn: () => void) => { clearTimeout(limit); a.removeAttribute('src'); a.load(); fn(); };
    const limit = setTimeout(() => done(() => rej(new Error('วัดความยาวเสียงไม่ได้ (โหลดไฟล์เสียงช้าเกินไป)'))), 20_000);
    a.onloadedmetadata = () => {
      const d = a.duration;
      done(() => Number.isFinite(d) && d > 0 ? res(d) : rej(new Error('วัดความยาวเสียงไม่ได้')));
    };
    a.onerror = () => done(() => rej(new Error('เปิดไฟล์เสียงที่สร้างไม่ได้')));
    a.src = url;
  });
}

export function initVideoVoice(ctx: VideoCtx, opts: { isAdmin: () => boolean; pause: () => void }) {
  let info: VoiceInfo | null = null;
  let infoError = '';
  let elevenVoices: Array<{ id: string; name: string; about: string }> | null = null;
  let elevenError = '';
  let provider: VoiceProvider = 'gemini';
  const making = new Set<string>();          // clip ids whose voice is being made
  let allRunning = false;
  const listen = new Audio();
  let listening: string | null = null;      // clip id being played

  const say = (t: string) => { $('vvMsg').textContent = t; $('vvMsg').hidden = !t; };
  const can = () => !!info?.canUse && !ctx.readonly();

  async function load() {
    try { info = await api.voice.info(); infoError = ''; }
    catch (e) { info = null; infoError = errText(e); }
    const p = ctx.project();
    if (p?.narration.choice) provider = p.narration.choice.provider;
    if (provider === 'eleven') await loadEleven();
    paint();
  }
  async function loadEleven() {
    if (elevenVoices || !info?.canUse || info.services.eleven.problem) return;
    try { elevenVoices = (await api.voice.elevenVoices()).voices; elevenError = elevenVoices.length ? '' : 'บัญชี ElevenLabs นี้ยังไม่มีเสียง เพิ่มเสียง (เช่น จาก Voice Library) ที่ elevenlabs.io ก่อน'; }
    catch (e) { elevenVoices = null; elevenError = 'โหลดรายการเสียงของ ElevenLabs ไม่ได้: ' + errText(e); }
  }

  /** the voices of a service, as choices */
  function voicesOf(pv: VoiceProvider): VoiceChoice[] {
    if (pv === 'gemini') return (info?.geminiVoices ?? []).map(v => ({ provider: 'gemini', voice: v.id, label: `${v.id} · ${v.th}`, style: '' }));
    return (elevenVoices ?? []).map(v => ({ provider: 'eleven', voice: v.id, label: `${v.name}${v.about ? ' · ' + v.about : ''}`.slice(0, 80), style: '' }));
  }

  const STATE: Record<VoiceState, string> = {
    none: 'ยังไม่มีข้อความพากย์', missing: 'ยังไม่ได้สร้างเสียง', stale: 'แก้ข้อความหรือเปลี่ยนเสียงแล้ว ต้องสร้างเสียงใหม่', ready: 'เสียงพร้อม'
  };

  function paint() {
    const p = ctx.project();
    if (!p) return;
    // this month's use, and why it cannot be used (no right, a key not set)
    $('vvUse').textContent = info
      ? `เดือนนี้ทั้งกลุ่มใช้ไป $${usd(info.usedUsd)} จากเพดาน $${usd(info.capUsd)} · Gemini ราว $${info.prices.geminiUsdPerMinute.toFixed(3)} ต่อเสียง 1 นาที · ElevenLabs $${info.prices.elevenUsdPer1000Chars.toFixed(2)} ต่อ 1,000 ตัวอักษร`
        + (info.canUse ? '' : ' · บัญชีนี้ยังไม่มีสิทธิ์ "เสียงพากย์ AI" (ขอผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้) ยังพิมพ์ข้อความไว้ก่อนได้')
      : `โหลดข้อมูลเสียงพากย์ไม่ได้: ${infoError || 'กำลังโหลด…'}`;
    setPressed('vvProv', b => b.dataset.v === provider);
    const problem = info ? info.services[provider].problem ?? (provider === 'eleven' ? elevenError : '') : '';
    $('vvProb').textContent = problem; $('vvProb').hidden = !problem;
    // the video's voice
    const sel = $('vvVoiceSel') as HTMLSelectElement;
    const list = voicesOf(provider), cur = p.narration.choice;
    sel.textContent = '';
    sel.append(new Option(list.length ? '— เลือกเสียง —' : provider === 'eleven' && !elevenVoices ? 'กำลังโหลดเสียงของ ElevenLabs…' : 'ไม่มีเสียงให้เลือก', ''));
    for (const v of list) sel.append(new Option(v.label, v.voice));
    sel.value = cur && cur.provider === provider ? cur.voice : '';
    if (cur && cur.provider === provider && sel.value !== cur.voice) { sel.append(new Option(cur.label || cur.voice, cur.voice)); sel.value = cur.voice; }
    sel.disabled = ctx.readonly();
    const style = $in('vvStyle');
    style.hidden = $('vvStyleLbl').hidden = provider !== 'gemini';
    if (document.activeElement !== style) style.value = cur?.provider === 'gemini' ? cur.style : '';
    style.disabled = ctx.readonly();
    $('vvProvNote').textContent = provider === 'gemini'
      ? `Gemini (${info?.services.gemini.model ?? ''}) พูดไทยได้ สั่งวิธีพูดเป็นข้อความได้`
      : `ElevenLabs (${info?.services.eleven.model ?? 'eleven_v3'}) — รุ่นเดียวที่พูดไทยได้ · ใส่อารมณ์ในข้อความได้ด้วยแท็กภาษาอังกฤษ เช่น [excited]`;
    $in('vvSubs').checked = p.narration.subs; $in('vvSubs').disabled = ctx.readonly();
    const todo = p.clips.filter(c => { const s = voiceState(p, c); return s === 'missing' || s === 'stale'; }).length;
    const all = $('vvAll') as HTMLButtonElement;
    all.textContent = allRunning ? 'กำลังสร้างเสียง…' : todo ? `สร้างเสียง ${todo} ฉากที่ยังไม่มี/ต้องสร้างใหม่` : 'ทุกฉากที่มีข้อความมีเสียงแล้ว';
    all.disabled = allRunning || !todo || !can();
    paintList(p);
    $('vvCapRow').hidden = !opts.isAdmin();
    if (info && document.activeElement !== $('vvCap')) $in('vvCap').value = String(info.capUsd);
  }

  /** one row a scene; a row being typed in is never rebuilt (BUG-20: the keyboard would close mid-word) */
  function paintList(p: VideoProject) {
    const ol = $('vvList');
    const typing = (document.activeElement as HTMLElement | null)?.closest?.('#vvList li') as HTMLElement | null;
    const keep = typing?.dataset.id ?? null;
    const rows = new Map([...ol.children].map(li => [(li as HTMLElement).dataset.id!, li as HTMLElement]));
    const ids = p.clips.map(c => c.id);
    for (const [id, li] of rows) if (!ids.includes(id)) li.remove();
    p.clips.forEach((c, k) => {
      let li = rows.get(c.id);
      if (!li) { li = buildRow(c.id); }
      ol.append(li);   // keeps order (append moves an existing row)
      if (c.id !== keep) fillRow(li, p, c, k); else fillState(li, p, c, k);
    });
  }

  function buildRow(id: string): HTMLElement {
    const li = document.createElement('li');
    li.dataset.id = id;
    li.innerHTML = `<b></b><textarea rows="3" aria-label="ข้อความพากย์"></textarea><span class="vvstate"></span>
      <div class="vvrow"><button class="btn main vvmake">สร้างเสียง</button><button class="btn vvplay">▶ ฟัง</button><select class="vvown" aria-label="เสียงของฉากนี้"></select></div>`;
    const ta = li.querySelector('textarea')!;
    ta.maxLength = VOICE_TEXT_MAX;
    ta.onchange = () => {
      const p = ctx.project(); if (!p) return;
      const k = p.clips.findIndex(c => c.id === id);
      if (k < 0) { say('ไม่พบฉากนี้แล้ว (อาจถูกลบหรือเลิกทำไป) ข้อความไม่ได้บันทึก'); return; }
      if ((p.clips[k].voice?.text ?? '') === ta.value) return;
      if (!ctx.commit(setVoiceText(p, k, ta.value))) { paint(); return; }
    };
    ta.oninput = () => fillState(li, ctx.project()!, null, -1, ta.value);
    (li.querySelector('.vvmake') as HTMLButtonElement).onclick = () => void make(id);
    (li.querySelector('.vvplay') as HTMLButtonElement).onclick = () => play(id);
    (li.querySelector('.vvown') as HTMLSelectElement).onchange = e => {
      const p = ctx.project(); if (!p) return;
      const k = p.clips.findIndex(c => c.id === id); if (k < 0) return;
      const v = (e.target as HTMLSelectElement).value;
      const pick = v ? voicesOf(p.narration.choice?.provider ?? provider).find(x => x.voice === v) ?? null : null;
      if (!ctx.commit(setClipVoiceChoice(p, k, pick && { ...pick, style: p.narration.choice?.style ?? '' }))) paint();
    };
    return li;
  }

  function fillRow(li: HTMLElement, p: VideoProject, c: VideoClip, k: number) {
    const ta = li.querySelector('textarea')!;
    if (ta.value !== (c.voice?.text ?? '')) ta.value = c.voice?.text ?? '';
    ta.disabled = ctx.readonly();
    const own = li.querySelector('.vvown') as HTMLSelectElement;
    const base = p.narration.choice;
    own.textContent = '';
    own.append(new Option(base ? `เสียงของวิดีโอ (${base.label.split(' · ')[0]})` : 'เสียงของวิดีโอ (ยังไม่ได้เลือก)', ''));
    for (const v of voicesOf(base?.provider ?? provider)) own.append(new Option(v.label, v.voice));
    own.value = c.voice?.choice?.voice ?? '';
    own.disabled = ctx.readonly();
    fillState(li, p, c, k);
  }

  /** the scene's title, state and buttons (also while its words are being typed: `typed`) */
  function fillState(li: HTMLElement, p: VideoProject, cIn: VideoClip | null, kIn: number, typed?: string) {
    const k = kIn >= 0 ? kIn : p.clips.findIndex(c => c.id === li.dataset.id);
    const c = cIn ?? p.clips[k];
    if (!c) return;
    const text = typed ?? c.voice?.text ?? '';
    const state: VoiceState = typed !== undefined && typed !== (c.voice?.text ?? '') ? (text.trim() ? (c.voice?.audio ? 'stale' : 'missing') : 'none') : voiceState(p, c);
    li.querySelector('b')!.textContent = `ฉากที่ ${k + 1}${c.slot?.label ? ` · ${c.slot.label}` : ''} · ${clipLength(c).toFixed(1)} วิ`;
    const st = li.querySelector('.vvstate')!;
    const busy = making.has(c.id);
    st.textContent = busy ? 'กำลังสร้างเสียง… (ราว 5–30 วินาที)'
      : `${STATE[state]}${c.voice?.audio && c.voice.dur ? ` · เสียงยาว ${c.voice.dur.toFixed(1)} วิ` : ''}${(c.voice?.extra ?? 0) > 0 ? ` · ฉากยืดให้ ${c.voice!.extra.toFixed(1)} วิ` : ''} · ${[...text].length}/${VOICE_TEXT_MAX}`;
    st.classList.toggle('warn', state === 'missing' || state === 'stale');
    const mk = li.querySelector('.vvmake') as HTMLButtonElement;
    mk.textContent = busy ? 'กำลังสร้าง…' : state === 'ready' ? 'สร้างใหม่' : 'สร้างเสียง';
    mk.disabled = busy || !text.trim() || !can();
    const pl = li.querySelector('.vvplay') as HTMLButtonElement;
    pl.disabled = !c.voice?.audio;
    pl.textContent = listening === c.id ? '■ หยุด' : '▶ ฟัง';
    li.classList.toggle('cur', busy);
  }

  /** make the voice of the scene with this clip id; true when it was put in */
  async function make(id: string): Promise<boolean> {
    const p0 = ctx.project(); if (!p0) return false;
    if (!info?.canUse) { say('บัญชีนี้ยังไม่มีสิทธิ์ "เสียงพากย์ AI" ขอผู้ดูแลเปิดให้ที่ หลังบ้านผู้ดูแล → ผู้ใช้'); return false; }
    // words typed but not yet committed (the box still has the focus) are taken first
    const li = $('vvList').querySelector<HTMLElement>(`li[data-id="${id}"]`);
    const ta = li?.querySelector('textarea');
    let k = p0.clips.findIndex(c => c.id === id);
    if (k < 0) { say('ไม่พบฉากนี้แล้ว (อาจถูกลบหรือเลิกทำไป)'); return false; }
    if (ta && ta.value !== (p0.clips[k].voice?.text ?? '') && !ctx.commit(setVoiceText(p0, k, ta.value))) return false;
    const p = ctx.project()!;
    k = p.clips.findIndex(c => c.id === id);
    const c = p.clips[k], choice = voiceOf(p, c), text = c.voice?.text.trim() ?? '';
    if (!text) { say(`ฉากที่ ${k + 1} ยังไม่มีข้อความที่จะให้พูด`); return false; }
    if (!choice) { say('ยังไม่ได้เลือกเสียงของวิดีโอ เลือกบริการและเสียงด้านบนก่อน'); return false; }
    const problem = info.services[choice.provider].problem;
    if (problem) { say(problem); return false; }
    const sig = voiceSig(choice, text), work = ctx.saver.openedCount;
    making.add(id); say(''); paint();
    try {
      const r = await api.voice.make({ provider: choice.provider, voice: choice.voice, style: choice.style, text });
      if (info) { info.usedUsd = r.usedUsd; info.capUsd = r.capUsd; }
      const dur = r.voice.seconds ?? await measure(api.voice.fileUrl(r.voice.id));
      if (ctx.saver.openedCount !== work) throw new Error('เปิดงานอื่นไประหว่างรอ เสียงที่สร้างแล้วไม่ได้ใส่ (นับในเพดานแล้ว)');
      const now = ctx.project()!;
      const at = now.clips.findIndex(x => x.id === id);
      if (at < 0) throw new Error(`ฉากที่ ${k + 1} ถูกลบระหว่างรอ เสียงที่สร้างแล้วไม่ได้ใส่ (นับในเพดานแล้ว)`);
      // the words or the voice may have changed while waiting: the voice is still put in, and shows as "ต้องสร้างใหม่"
      if (!ctx.commit(setVoiceAudio(now, at, r.voice.id, dur, sig))) return false;
      toast(`สร้างเสียงฉากที่ ${at + 1} แล้ว (${dur.toFixed(1)} วิ · ราว $${r.voice.costUsd.toFixed(4)})`);
      return true;
    } catch (e) {
      const why = e instanceof ApiError && e.status === 429 ? e.message : `สร้างเสียงฉากที่ ${k + 1} ไม่สำเร็จ: ${errText(e)}`;
      say(why); toast(why, true);
      return false;
    } finally {
      making.delete(id);
      if (isOpen('shVVoice')) paint();
    }
  }

  function play(id: string) {
    const p = ctx.project(); const c = p?.clips.find(x => x.id === id);
    if (listening === id) { listen.pause(); listening = null; paint(); return; }
    if (!c?.voice?.audio) return;
    opts.pause();
    listening = id;
    listen.src = api.voice.fileUrl(c.voice.audio);
    listen.onended = () => { listening = null; paint(); };
    listen.onerror = () => { listening = null; paint(); say('เปิดไฟล์เสียงนี้ไม่ได้ (ต้องออนไลน์) ถ้ายังไม่ได้ ให้สร้างเสียงใหม่'); };
    listen.play().catch(e => { listening = null; paint(); say('เล่นเสียงไม่ได้: ' + errText(e)); });
    paint();
  }

  /* ---------- the video's voice ---------- */
  $('vvProv').onclick = async e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    provider = b.dataset.v as VoiceProvider;
    if (provider === 'eleven') { paint(); await loadEleven(); }
    paint();
  };
  $('vvVoiceSel').onchange = () => {
    const p = ctx.project(); if (!p) return;
    const v = ($('vvVoiceSel') as HTMLSelectElement).value;
    const pick = voicesOf(provider).find(x => x.voice === v) ?? null;
    const style = provider === 'gemini' ? $in('vvStyle').value.trim() : '';
    if (!ctx.commit(setNarration(p, { choice: pick && { ...pick, style } }))) paint();
  };
  $in('vvStyle').onchange = () => {
    const p = ctx.project(); if (!p?.narration.choice || p.narration.choice.provider !== 'gemini') return;
    if (!ctx.commit(setNarration(p, { choice: { ...p.narration.choice, style: $in('vvStyle').value.trim() } }))) paint();
  };
  $in('vvSubs').onchange = () => {
    const p = ctx.project(); if (!p) return;
    if (!ctx.commit(setNarration(p, { subs: $in('vvSubs').checked }))) paint();
    else toast($in('vvSubs').checked ? 'ใส่ซับไตเติลจากข้อความพากย์แล้ว' : 'เอาซับไตเติลจากข้อความพากย์ออกแล้ว');
  };
  /** every scene with words and no (matching) voice, one after another; stops at the first that fails, says which */
  $('vvAll').onclick = async () => {
    const p = ctx.project(); if (!p || allRunning) return;
    const ids = p.clips.filter(c => { const s = voiceState(p, c); return s === 'missing' || s === 'stale'; }).map(c => c.id);
    allRunning = true; paint();
    let done = 0;
    try {
      for (const id of ids) { if (!(await make(id))) break; done++; }
    } finally { allRunning = false; paint(); }
    if (done === ids.length) toast(`สร้างเสียงครบ ${done} ฉากแล้ว`);
    else if (done) toast(`สร้างเสียงได้ ${done} จาก ${ids.length} ฉาก แล้วหยุด (ดูเหตุผลในหน้าพากย์)`, true);
  };
  $('vvCapSave').onclick = async () => {
    const n = Number($in('vvCap').value.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n) || n < 0) { say('เพดานต้องเป็นตัวเลข 0 ขึ้นไป (0 = ปิดเสียงพากย์ AI)'); return; }
    try { const r = await api.voice.setCap(n); if (info) info.capUsd = r.capUsd; toast(`ตั้งเพดานเสียงพากย์ AI เป็น $${r.capUsd.toFixed(2)} ต่อเดือนแล้ว`); say(''); }
    catch (e) { say('บันทึกเพดานไม่สำเร็จ: ' + errText(e)); }
    paint();
  };

  return {
    /** open the panel (scene under the playhead first in view) */
    open(scrollTo: number) {
      if (!ctx.project()) return;
      opts.pause(); say('');
      openSheet('shVVoice');
      paint();
      void load().then(() => {
        const id = ctx.project()?.clips[scrollTo]?.id;
        if (id) $('vvList').querySelector(`li[data-id="${id}"]`)?.scrollIntoView({ block: 'nearest' });
      });
    },
    /** the work changed (an edit, undo, another work): the panel follows */
    sync() { if (isOpen('shVVoice')) paint(); },
    /** no longer this work: stop listening */
    stop() { listen.pause(); listening = null; }
  };
}
